import React, { useEffect, useRef, useState } from 'react';
import { Modal, Divider } from "antd";
import { useDispatch, useSelector } from "react-redux";
import { useLoadScript, GoogleMap, Marker, Polygon } from "@react-google-maps/api";
import { isCheckInLocationInOffice, getLocationDetails } from "../../utils/locationUtils";
import { formatDateTime } from "../../utils/dateUtils";
import { getEmployeeProfile } from "../data/redux/employeesSlice";
import { BASE_URLS } from "../data/redux/apis/urls";

const AttendanceDetailsModel = (props: any) => {

    const { item, show, close } = props;
    const dispatch = useDispatch();
    const [currentItem, setCurrentItem] = React.useState<any>(null);
    const [showModal, setShowModal] = React.useState(false);
    const [officePolygons, setOfficePolygons] = React.useState<Array<Array<{ lat: number; lng: number }>>>([]);
    const [offices, setOffices] = React.useState<Array<{ id: number; name: string; polygons: Array<Array<{ lat: number; lng: number }>> }>>([]);
    const employeesState = useSelector((state: any) => state.employeesState);
    const API_KEY = "AIzaSyAqQuX2O1PHI9uihjJnOrYHwkkyxdNcfNo";

    const { isLoaded, loadError } = useLoadScript({
        googleMapsApiKey: API_KEY,
        libraries: ["places", "drawing", "geometry"] as any,
    });

    useEffect(() => {
        if (item) {
            setCurrentItem(item);
        }
    }, [item]);

    // Fetch employee profile to get offices
    useEffect(() => {
        // Reset polygons when item changes
        setOfficePolygons([]);
        setOffices([]);

        if (!currentItem) return;

        // Get employee ID from various possible fields
        const employeeId =
            currentItem?.employee_id ||
            currentItem?.employee?.id ||
            currentItem?.employee?.employee_id ||
            currentItem?.emp_id;

        console.log('=== Fetching Employee Profile ===');
        console.log('Employee ID:', employeeId);
        console.log('Current Item:', currentItem);

        if (employeeId) {
            dispatch(getEmployeeProfile({ empId: employeeId }));
        } else {
            console.warn('❌ No employee ID found in attendance item');
        }
    }, [currentItem, dispatch]);

    // Process employee profile response to extract offices
    useEffect(() => {
        if (employeesState?.loading === "fulfilled" && employeesState?.action === "employee-profile") {
            // API response structure: { status: 200, message: "...", data: { offices: [...] } }
            // Redux stores it in controllingOfficerData
            const responseData = employeesState?.controllingOfficerData;
            console.log('🔍 Full employeesState:', employeesState);
            console.log('🔍 controllingOfficerData:', responseData);
            console.log('🔍 responseData?.data:', responseData?.data);
            console.log('🔍 responseData?.data?.data:', responseData?.data?.data);

            // Try multiple paths to find the data
            const profileData = responseData?.data?.data || responseData?.data || responseData;
            console.log('✅ Using profileData:', profileData);
            console.log('✅ profileData?.offices:', profileData?.offices);

            if (profileData?.offices && Array.isArray(profileData.offices) && profileData.offices.length > 0) {
                console.log('📍 Found', profileData.offices.length, 'office(s)');

                const processedOffices: Array<{ id: number; name: string; polygons: Array<Array<{ lat: number; lng: number }>> }> = [];
                const allPolygons: Array<Array<{ lat: number; lng: number }>> = [];

                // Process each office
                profileData.offices.forEach((office: any, officeIndex: number) => {
                    console.log(`Processing office ${officeIndex + 1}:`, office.name, office.id);

                    if (office.coordinates && Array.isArray(office.coordinates) && office.coordinates.length > 0) {
                        const officePolygons: Array<Array<{ lat: number; lng: number }>> = [];

                        // Process each polygon in this office's coordinates
                        office.coordinates.forEach((polygonCoords: any, polygonIndex: number) => {
                            if (Array.isArray(polygonCoords) && polygonCoords.length > 0) {
                                // Convert from [lng, lat] format to {lat, lng} format
                                const convertedPolygon = polygonCoords
                                    .map((coord: any) => {
                                        // Handle both [lng, lat] array and {lat, lng} object formats
                                        if (Array.isArray(coord) && coord.length >= 2) {
                                            return {
                                                lat: parseFloat(coord[1]), // lat is second element
                                                lng: parseFloat(coord[0])  // lng is first element
                                            };
                                        } else if (coord && typeof coord === 'object' && coord.lat !== undefined && coord.lng !== undefined) {
                                            return {
                                                lat: parseFloat(coord.lat),
                                                lng: parseFloat(coord.lng)
                                            };
                                        }
                                        return null;
                                    })
                                    .filter((coord: any) => coord !== null && !isNaN(coord.lat) && !isNaN(coord.lng)) as Array<{ lat: number; lng: number }>;

                                if (convertedPolygon.length >= 3) {
                                    officePolygons.push(convertedPolygon);
                                    allPolygons.push(convertedPolygon);
                                    console.log(`  ✅ Polygon ${polygonIndex + 1}: ${convertedPolygon.length} points`);
                                } else {
                                    console.warn(`  ⚠️ Polygon ${polygonIndex + 1}: Invalid (need at least 3 points, got ${convertedPolygon.length})`);
                                }
                            }
                        });

                        if (officePolygons.length > 0) {
                            processedOffices.push({
                                id: office.id,
                                name: office.name,
                                polygons: officePolygons
                            });
                        }
                    } else {
                        console.warn(`⚠️ Office ${office.name} has no valid coordinates`);
                    }
                });

                if (processedOffices.length > 0) {
                    setOffices(processedOffices);
                    setOfficePolygons(allPolygons);
                    console.log('✅ Processed', processedOffices.length, 'office(s) with', allPolygons.length, 'total polygon(s)');
                } else {
                    console.warn('❌ No valid offices with polygons found');
                }
            } else {
                console.warn('❌ No offices found in employee profile');
            }
        } else if (employeesState?.loading === "rejected" && employeesState?.action === "employee-profile") {
            console.error('❌ Failed to fetch employee profile:', employeesState?.error);
        }
    }, [employeesState]);
    useEffect(() => {
        setShowModal(item);
    }, [show]);

    return (
        <>
            <Modal
                title={
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <span style={{ fontSize: '24px' }}>📍</span>
                        <div>
                            <h4 style={{ margin: 0, fontWeight: 'bold', color: '#1a1a1a', fontSize: '18px' }}>
                                Attendance Details
                            </h4>
                            {currentItem?.employee?.details?.firstname && (
                                <p style={{ margin: '4px 0 0 0', color: '#666', fontSize: '13px', fontWeight: 'normal' }}>
                                    {currentItem.employee.details.firstname}
                                </p>
                            )}
                        </div>
                    </div>
                }
                open={showModal}
                onCancel={() => {
                    setShowModal(false);
                    close();
                    setOfficePolygons([]);
                }}
                footer={false}
                width={700}
                style={{ top: 20 }}
            >
                <div className="row">
                    <div className="col-md-12" style={{ height: 350, marginBottom: '20px', borderRadius: '8px', overflow: 'hidden', border: '1px solid #e0e0e0' }}>
                        {currentItem?.check_in_latitude && currentItem?.check_in_longitude ? (
                            (() => {
                                const checkInLat = parseFloat(currentItem.check_in_latitude);
                                const checkInLng = parseFloat(currentItem.check_in_longitude);
                                const checkInPosition = { lat: checkInLat, lng: checkInLng };

                                // If no office_location_id, show iframe map (original preview map)
                                if (!currentItem?.office_location_id && !item?.office_location_id) {
                                    return (
                                        <iframe
                                            src={`${BASE_URLS.BASE_URL}map?lat=${currentItem.check_in_latitude}&long=${currentItem.check_in_longitude}`}
                                            title="Check In Location Map"
                                            style={{ width: '100%', height: '100%', border: 'none' }}
                                            allowFullScreen
                                            sandbox="allow-scripts allow-same-origin allow-forms allow-downloads"
                                        />
                                    );
                                }

                                // If office_location_id exists, show Google Map with multiple polygons
                                // Calculate map center (use first polygon center if available, otherwise check-in location)
                                const mapCenter = officePolygons && officePolygons.length > 0 && officePolygons[0] && officePolygons[0].length > 0
                                    ? (() => {
                                        const firstPolygon = officePolygons[0];
                                        const center = firstPolygon.reduce((acc, point) => ({
                                            lat: acc.lat + point.lat,
                                            lng: acc.lng + point.lng
                                        }), { lat: 0, lng: 0 });
                                        return {
                                            lat: center.lat / firstPolygon.length,
                                            lng: center.lng / firstPolygon.length
                                        };
                                    })()
                                    : checkInPosition;

                                if (loadError) {
                                    return (
                                        <div style={{
                                            width: '100%',
                                            height: '100%',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            backgroundColor: '#fff5f5',
                                            color: 'red'
                                        }}>
                                            <div style={{ textAlign: 'center' }}>
                                                <div style={{ fontSize: '48px', marginBottom: '10px' }}>⚠️</div>
                                                <p>Map load error. Please try again.</p>
                                            </div>
                                        </div>
                                    );
                                }

                                if (!isLoaded) {
                                    return (
                                        <div style={{
                                            width: '100%',
                                            height: '100%',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            backgroundColor: '#f5f5f5'
                                        }}>
                                            <div style={{ textAlign: 'center' }}>
                                                <div className="spinner-border text-primary" role="status">
                                                    <span className="sr-only">Loading...</span>
                                                </div>
                                                <p style={{ marginTop: '10px', color: '#666' }}>Loading Google Map...</p>
                                            </div>
                                        </div>
                                    );
                                }

                                // Check if check-in location is in any of the polygons
                                const isInOffice = officePolygons && officePolygons.length > 0
                                    ? officePolygons.some(polygon =>
                                        polygon && polygon.length >= 3 && isCheckInLocationInOffice(checkInLat, checkInLng, polygon)
                                    )
                                    : false;

                                // Different colors for different polygons
                                const polygonColors = [
                                    { fill: '#1976D2', stroke: '#1976D2' }, // Blue
                                    { fill: '#28a745', stroke: '#28a745' }, // Green
                                    { fill: '#ffc107', stroke: '#ffc107' }, // Yellow
                                    { fill: '#dc3545', stroke: '#dc3545' }, // Red
                                    { fill: '#6f42c1', stroke: '#6f42c1' }, // Purple
                                ];

                                return (
                                    <GoogleMap
                                        mapContainerStyle={{ width: '100%', height: '100%' }}
                                        zoom={officePolygons && officePolygons.length > 0 && officePolygons[0]?.length >= 3 ? 17 : 15}
                                        center={mapCenter}
                                        options={{
                                            mapTypeControl: true,
                                            streetViewControl: true,
                                            fullscreenControl: true,
                                            zoomControl: true,
                                        }}
                                    >
                                        {/* Show all office polygons if available */}
                                        {officePolygons && officePolygons.length > 0 && officePolygons.map((polygon, index) => {
                                            if (!polygon || polygon.length < 3) return null;
                                            const colorIndex = index % polygonColors.length;
                                            const colors = polygonColors[colorIndex];
                                            return (
                                                <Polygon
                                                    key={`polygon-${index}`}
                                                    paths={polygon}
                                                    options={{
                                                        fillColor: colors.fill,
                                                        fillOpacity: 0.2,
                                                        strokeColor: colors.stroke,
                                                        strokeWeight: 3,
                                                        clickable: false,
                                                        draggable: false,
                                                        editable: false,
                                                        geodesic: false,
                                                        zIndex: 1
                                                    }}
                                                />
                                            );
                                        })}

                                        {/* Show check-in location marker */}
                                        {(() => {
                                            const iconConfig: any = {
                                                url: isInOffice
                                                    ? 'http://maps.google.com/mapfiles/ms/icons/green-dot.png'
                                                    : 'http://maps.google.com/mapfiles/ms/icons/red-dot.png',
                                            };

                                            if (window.google && window.google.maps) {
                                                iconConfig.scaledSize = new window.google.maps.Size(40, 40);
                                            }

                                            return (
                                                <Marker
                                                    position={checkInPosition}
                                                    label={{
                                                        text: isInOffice ? '✓ In Office' : '✗ Out of Office',
                                                        color: 'white',
                                                        fontWeight: 'bold',
                                                        fontSize: '12px'
                                                    }}
                                                    icon={iconConfig}
                                                />
                                            );
                                        })()}
                                    </GoogleMap>
                                );
                            })()
                        ) : (
                            <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: '#f5f5f5' }}>
                                <div style={{ textAlign: 'center' }}>
                                    <div style={{ fontSize: '48px', marginBottom: '10px' }}>📍</div>
                                    <p style={{ color: '#666', margin: 0 }}>Location data not available</p>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
                {/* Main Content Sections */}
                <div style={{ marginTop: '20px' }}>
                    {/* Basic Information Section */}
                    <div style={{
                        backgroundColor: '#f8f9fa',
                        padding: '16px',
                        borderRadius: '8px',
                        marginBottom: '20px',
                        border: '1px solid #e9ecef'
                    }}>
                        <h5 style={{
                            margin: '0 0 16px 0',
                            color: '#1a1a1a',
                            fontWeight: '600',
                            fontSize: '15px',
                            borderBottom: '2px solid #007bff',
                            paddingBottom: '8px'
                        }}>
                            📋 Basic Information
                        </h5>
                        <div className="row">
                            <div className="col-md-6">
                                <div style={{ marginBottom: '14px' }}>
                                    <span style={{
                                        display: 'block',
                                        fontSize: '12px',
                                        color: '#6c757d',
                                        fontWeight: '500',
                                        marginBottom: '4px',
                                        textTransform: 'uppercase',
                                        letterSpacing: '0.5px'
                                    }}>
                                        Office Name
                                    </span>
                                    <p style={{
                                        margin: 0,
                                        color: '#212529',
                                        fontSize: '14px',
                                        fontWeight: '500'
                                    }}>
                                        {currentItem?.officeLocation?.name || '-'}
                                    </p>
                                </div>
                            </div>
                            <div className="col-md-6">
                                <div style={{ marginBottom: '14px' }}>
                                    <span style={{
                                        display: 'block',
                                        fontSize: '12px',
                                        color: '#6c757d',
                                        fontWeight: '500',
                                        marginBottom: '4px',
                                        textTransform: 'uppercase',
                                        letterSpacing: '0.5px'
                                    }}>
                                        Working Hours
                                    </span>
                                    <p style={{
                                        margin: 0,
                                        color: '#212529',
                                        fontSize: '14px',
                                        fontWeight: '500'
                                    }}>
                                        {currentItem?.working_hours || "-"}
                                    </p>
                                </div>
                            </div>
                            <div className="col-md-6">
                                <div style={{ marginBottom: '14px' }}>
                                    <span style={{
                                        display: 'block',
                                        fontSize: '12px',
                                        color: '#6c757d',
                                        fontWeight: '500',
                                        marginBottom: '4px',
                                        textTransform: 'uppercase',
                                        letterSpacing: '0.5px'
                                    }}>
                                        Attendance Status
                                    </span>
                                    <p style={{
                                        margin: 0,
                                        color: '#212529',
                                        fontSize: '14px',
                                        fontWeight: '500'
                                    }}>
                                        {currentItem?.attendance_status
                                            ?.replace(/_/g, " ")
                                            .replace(/\b\w/g, (c: any) => c.toUpperCase()) || "-"}
                                    </p>
                                </div>
                            </div>
                            <div className="col-md-6">
                                <div style={{ marginBottom: '14px' }}>
                                    <span style={{
                                        display: 'block',
                                        fontSize: '12px',
                                        color: '#6c757d',
                                        fontWeight: '500',
                                        marginBottom: '4px',
                                        textTransform: 'uppercase',
                                        letterSpacing: '0.5px'
                                    }}>
                                        Verification
                                    </span>
                                    <p style={{
                                        margin: 0,
                                        color: '#212529',
                                        fontSize: '14px',
                                        fontWeight: '500'
                                    }}>
                                        {currentItem?.is_verified || "-"}
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Time Information Section */}
                    <div style={{
                        backgroundColor: '#f8f9fa',
                        padding: '16px',
                        borderRadius: '8px',
                        marginBottom: '20px',
                        border: '1px solid #e9ecef'
                    }}>
                        <h5 style={{
                            margin: '0 0 16px 0',
                            color: '#1a1a1a',
                            fontWeight: '600',
                            fontSize: '15px',
                            borderBottom: '2px solid #28a745',
                            paddingBottom: '8px'
                        }}>
                            ⏰ Time Information
                        </h5>
                        <div className="row">
                            <div className="col-md-6">
                                <div style={{ marginBottom: '14px' }}>
                                    <span style={{
                                        display: 'block',
                                        fontSize: '12px',
                                        color: '#6c757d',
                                        fontWeight: '500',
                                        marginBottom: '4px',
                                        textTransform: 'uppercase',
                                        letterSpacing: '0.5px'
                                    }}>
                                        Check In Time
                                    </span>
                                    <p style={{
                                        margin: 0,
                                        color: '#212529',
                                        fontSize: '14px',
                                        fontWeight: '500'
                                    }}>
                                        {currentItem?.check_in_time
                                            ? formatDateTime(currentItem.check_in_time)
                                            : "-"}
                                    </p>
                                </div>
                            </div>
                            <div className="col-md-6">
                                <div style={{ marginBottom: '14px' }}>
                                    <span style={{
                                        display: 'block',
                                        fontSize: '12px',
                                        color: '#6c757d',
                                        fontWeight: '500',
                                        marginBottom: '4px',
                                        textTransform: 'uppercase',
                                        letterSpacing: '0.5px'
                                    }}>
                                        Check Out Time
                                    </span>
                                    <p style={{
                                        margin: 0,
                                        color: '#212529',
                                        fontSize: '14px',
                                        fontWeight: '500'
                                    }}>
                                        {currentItem?.check_out_time
                                            ? formatDateTime(currentItem.check_out_time)
                                            : "-"}
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Location Information Section */}
                    <div style={{
                        backgroundColor: '#f8f9fa',
                        padding: '16px',
                        borderRadius: '8px',
                        marginBottom: '20px',
                        border: '1px solid #e9ecef'
                    }}>
                        <h5 style={{
                            margin: '0 0 16px 0',
                            color: '#1a1a1a',
                            fontWeight: '600',
                            fontSize: '15px',
                            borderBottom: '2px solid #dc3545',
                            paddingBottom: '8px'
                        }}>
                            📍 Location Information
                        </h5>
                        <div className="row">
                            <div className="col-md-6">
                                <div style={{ marginBottom: '14px' }}>
                                    <span style={{
                                        display: 'block',
                                        fontSize: '12px',
                                        color: '#6c757d',
                                        fontWeight: '500',
                                        marginBottom: '4px',
                                        textTransform: 'uppercase',
                                        letterSpacing: '0.5px'
                                    }}>
                                        Check In Location
                                    </span>
                                    <p style={{ margin: 0, color: '#212529', fontSize: '13px' }}>
                                        <span style={{ fontWeight: '500' }}>Latitude:</span> {currentItem?.check_in_latitude || '-'}
                                        <br />
                                        <span style={{ fontWeight: '500' }}>Longitude:</span> {currentItem?.check_in_longitude || '-'}
                                    </p>
                                </div>
                            </div>
                            <div className="col-md-6">
                                <div style={{ marginBottom: '14px' }}>
                                    <span style={{
                                        display: 'block',
                                        fontSize: '12px',
                                        color: '#6c757d',
                                        fontWeight: '500',
                                        marginBottom: '4px',
                                        textTransform: 'uppercase',
                                        letterSpacing: '0.5px'
                                    }}>
                                        Check In Location Status
                                    </span>
                                    <div>
                                        {currentItem?.check_in_latitude && currentItem?.check_in_longitude ? (
                                            (() => {
                                                const lat = parseFloat(currentItem.check_in_latitude);
                                                const lng = parseFloat(currentItem.check_in_longitude);

                                                // If no office_location_id, show "Out of Office" message
                                                if (!currentItem?.office_location_id && !item?.office_location_id) {
                                                    return (
                                                        <div>
                                                            <span
                                                                style={{
                                                                    color: 'red',
                                                                    fontWeight: 'bold',
                                                                    fontSize: '14px'
                                                                }}
                                                            >
                                                                ✗ Out of Office
                                                            </span>
                                                            <div style={{ marginTop: '8px', fontSize: '12px', color: '#666' }}>
                                                                <div style={{ color: 'red', marginTop: '6px', padding: '6px', backgroundColor: '#fff5f5', borderRadius: '4px' }}>
                                                                    <small>
                                                                        <strong>Note:</strong> No office location assigned. This attendance was recorded without an office boundary reference.
                                                                    </small>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    );
                                                }

                                                // Check loading state
                                                if (employeesState?.loading === "pending" && employeesState?.action === "employee-profile") {
                                                    return <div style={{ color: '#666' }}>Loading office boundary data from API...</div>;
                                                }

                                                if (employeesState?.loading === "rejected" && employeesState?.action === "employee-profile") {
                                                    return <div style={{ color: 'red' }}>⚠️ Failed to load office boundary data: {employeesState?.error || 'Unknown error'}</div>;
                                                }

                                                // Check if we have polygon data
                                                if (!officePolygons || officePolygons.length === 0) {
                                                    return <div style={{ color: '#999' }}>No office boundary data available</div>;
                                                }

                                                // Check all polygons to find if location is in any of them
                                                let isInOffice = false;
                                                let locationDetails: any = null;

                                                for (const polygon of officePolygons) {
                                                    if (polygon && polygon.length >= 3) {
                                                        const details = getLocationDetails(lat, lng, polygon);
                                                        if (details && details.isInOffice) {
                                                            isInOffice = true;
                                                            locationDetails = details;
                                                            break; // Found in one polygon, no need to check others
                                                        }
                                                        // Keep the closest polygon details if not found in any
                                                        if (!locationDetails || (details && details.distanceFromNearestBoundary < locationDetails.distanceFromNearestBoundary)) {
                                                            locationDetails = details;
                                                        }
                                                    }
                                                }

                                                if (!locationDetails) {
                                                    return <div style={{ color: 'orange' }}>⚠️ Unable to calculate location details</div>;
                                                }

                                                const distanceFromCenter = (locationDetails.distanceFromCenter / 1000).toFixed(2);
                                                const distanceFromBoundary = (locationDetails.distanceFromNearestBoundary / 1000).toFixed(2);
                                                const distanceFromBoundaryMeters = locationDetails.distanceFromNearestBoundary.toFixed(0);
                                                return (
                                                    <div>
                                                        <span
                                                            style={{
                                                                color: isInOffice ? 'green' : 'red',
                                                                fontWeight: 'bold',
                                                                fontSize: '14px'
                                                            }}
                                                        >
                                                            {isInOffice ? '✓ In Office' : '✗ Out of Office'}
                                                        </span>
                                                        <div style={{ marginTop: '8px', fontSize: '12px', color: '#666' }}>
                                                            <div><b>Distance from Office Center:</b> {distanceFromCenter} km</div>
                                                            <div><b>Distance from Nearest Boundary:</b> {distanceFromBoundary} km ({distanceFromBoundaryMeters} m)</div>
                                                            {!isInOffice && (
                                                                <div style={{ color: 'red', marginTop: '6px', padding: '6px', backgroundColor: '#fff5f5', borderRadius: '4px' }}>
                                                                    <small>
                                                                        <strong>Why Out of Office:</strong> The check-in location ({lat.toFixed(6)}, {lng.toFixed(6)}) is outside the defined office boundary polygon. The location is approximately {distanceFromBoundaryMeters} meters away from the nearest office boundary point.
                                                                    </small>
                                                                </div>
                                                            )}
                                                            {isInOffice && (
                                                                <div style={{ color: 'green', marginTop: '6px', padding: '6px', backgroundColor: '#f0fff4', borderRadius: '4px' }}>
                                                                    <small>
                                                                        <strong>Why In Office:</strong> The check-in location ({lat.toFixed(6)}, {lng.toFixed(6)}) is within the office boundary polygon. The location is approximately {distanceFromCenter} km from the office center.
                                                                    </small>
                                                                </div>
                                                            )}
                                                            <div style={{ marginTop: '6px', fontSize: '11px', color: '#999', fontStyle: 'italic' }}>
                                                                Calculation method: Turf.js point-in-polygon algorithm (same as attendance map)
                                                            </div>
                                                        </div>
                                                    </div>
                                                );
                                            })()
                                        ) : (
                                            "-"
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="row">
                            <div className="col-md-6">
                                <div style={{ marginBottom: '14px' }}>
                                    <span style={{
                                        display: 'block',
                                        fontSize: '12px',
                                        color: '#6c757d',
                                        fontWeight: '500',
                                        marginBottom: '4px',
                                        textTransform: 'uppercase',
                                        letterSpacing: '0.5px'
                                    }}>
                                        Check Out Location
                                    </span>
                                    <p style={{ margin: 0, color: '#212529', fontSize: '13px' }}>
                                        <span style={{ fontWeight: '500' }}>Latitude:</span> {currentItem?.check_out_latitude || '-'}
                                        <br />
                                        <span style={{ fontWeight: '500' }}>Longitude:</span> {currentItem?.check_out_longitude || '-'}
                                    </p>
                                </div>
                            </div>
                            <div className="col-md-6">
                                <div style={{ marginBottom: '14px' }}>
                                    <span style={{
                                        display: 'block',
                                        fontSize: '12px',
                                        color: '#6c757d',
                                        fontWeight: '500',
                                        marginBottom: '4px',
                                        textTransform: 'uppercase',
                                        letterSpacing: '0.5px'
                                    }}>
                                        Check Out Location Status
                                    </span>
                                    <div>
                                        {currentItem?.check_out_latitude && currentItem?.check_out_longitude ? (
                                            (() => {
                                                const lat = parseFloat(currentItem.check_out_latitude);
                                                const lng = parseFloat(currentItem.check_out_longitude);

                                                // If no office_location_id, show "Out of Office" message
                                                if (!currentItem?.office_location_id && !item?.office_location_id) {
                                                    return (
                                                        <div>
                                                            <span
                                                                style={{
                                                                    color: 'red',
                                                                    fontWeight: 'bold',
                                                                    fontSize: '14px'
                                                                }}
                                                            >
                                                                ✗ Out of Office
                                                            </span>
                                                            <div style={{ marginTop: '8px', fontSize: '12px', color: '#666' }}>
                                                                <div style={{ color: 'red', marginTop: '6px', padding: '6px', backgroundColor: '#fff5f5', borderRadius: '4px' }}>
                                                                    <small>
                                                                        <strong>Note:</strong> No office location assigned. This attendance was recorded without an office boundary reference.
                                                                    </small>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    );
                                                }

                                                // Check loading state
                                                if (employeesState?.loading === "pending" && employeesState?.action === "employee-profile") {
                                                    return <div style={{ color: '#666' }}>Loading office boundary data from API...</div>;
                                                }

                                                if (employeesState?.loading === "rejected" && employeesState?.action === "employee-profile") {
                                                    return <div style={{ color: 'red' }}>⚠️ Failed to load office boundary data: {employeesState?.error || 'Unknown error'}</div>;
                                                }

                                                // Check if we have polygon data
                                                if (!officePolygons || officePolygons.length === 0) {
                                                    return <div style={{ color: '#999' }}>No office boundary data available</div>;
                                                }

                                                // Check all polygons to find if location is in any of them
                                                let isInOffice = false;
                                                let locationDetails: any = null;

                                                for (const polygon of officePolygons) {
                                                    if (polygon && polygon.length >= 3) {
                                                        const details = getLocationDetails(lat, lng, polygon);
                                                        if (details && details.isInOffice) {
                                                            isInOffice = true;
                                                            locationDetails = details;
                                                            break; // Found in one polygon, no need to check others
                                                        }
                                                        // Keep the closest polygon details if not found in any
                                                        if (!locationDetails || (details && details.distanceFromNearestBoundary < locationDetails.distanceFromNearestBoundary)) {
                                                            locationDetails = details;
                                                        }
                                                    }
                                                }

                                                if (!locationDetails) {
                                                    return <div style={{ color: 'orange' }}>⚠️ Unable to calculate location details</div>;
                                                }

                                                const distanceFromCenter = (locationDetails.distanceFromCenter / 1000).toFixed(2);
                                                const distanceFromBoundary = (locationDetails.distanceFromNearestBoundary / 1000).toFixed(2);
                                                const distanceFromBoundaryMeters = locationDetails.distanceFromNearestBoundary.toFixed(0);
                                                return (
                                                    <div>
                                                        <span
                                                            style={{
                                                                color: isInOffice ? 'green' : 'red',
                                                                fontWeight: 'bold',
                                                                fontSize: '14px'
                                                            }}
                                                        >
                                                            {isInOffice ? '✓ In Office' : '✗ Out of Office'}
                                                        </span>
                                                        <div style={{ marginTop: '8px', fontSize: '12px', color: '#666' }}>
                                                            <div><b>Distance from Office Center:</b> {distanceFromCenter} km</div>
                                                            <div><b>Distance from Nearest Boundary:</b> {distanceFromBoundary} km ({distanceFromBoundaryMeters} m)</div>
                                                            {!isInOffice && (
                                                                <div style={{ color: 'red', marginTop: '6px', padding: '6px', backgroundColor: '#fff5f5', borderRadius: '4px' }}>
                                                                    <small>
                                                                        <strong>Why Out of Office:</strong> The check-out location ({lat.toFixed(6)}, {lng.toFixed(6)}) is outside the defined office boundary polygon. The location is approximately {distanceFromBoundaryMeters} meters away from the nearest office boundary point.
                                                                    </small>
                                                                </div>
                                                            )}
                                                            {isInOffice && (
                                                                <div style={{ color: 'green', marginTop: '6px', padding: '6px', backgroundColor: '#f0fff4', borderRadius: '4px' }}>
                                                                    <small>
                                                                        <strong>Why In Office:</strong> The check-out location ({lat.toFixed(6)}, {lng.toFixed(6)}) is within the office boundary polygon. The location is approximately {distanceFromCenter} km from the office center.
                                                                    </small>
                                                                </div>
                                                            )}
                                                            <div style={{ marginTop: '6px', fontSize: '11px', color: '#999', fontStyle: 'italic' }}>
                                                                Calculation method: Turf.js point-in-polygon algorithm (same as attendance map)
                                                            </div>
                                                        </div>
                                                    </div>
                                                );
                                            })()
                                        ) : (
                                            "-"
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="row">
                            <div className="col-md-12">
                                <div style={{ marginBottom: '14px' }}>
                                    <span style={{
                                        display: 'block',
                                        fontSize: '12px',
                                        color: '#6c757d',
                                        fontWeight: '500',
                                        marginBottom: '4px',
                                        textTransform: 'uppercase',
                                        letterSpacing: '0.5px'
                                    }}>
                                        Office Boundary Info
                                    </span>
                                    <div style={{ fontSize: '12px' }}>
                                        {(!currentItem?.office_location_id && !item?.office_location_id) ? (
                                            <div style={{ color: '#999' }}>
                                                <small>No office location assigned</small>
                                            </div>
                                        ) : officePolygons && officePolygons.length > 0 ? (
                                            <>
                                                {offices.length > 0 ? (
                                                    <>
                                                        <div><b>Total Offices:</b> {offices.length}</div>
                                                        <div><b>Total Polygons:</b> {officePolygons.length}</div>
                                                        {offices.map((office, officeIndex) => (
                                                            <div key={`office-${office.id}`} style={{ marginTop: '12px', padding: '12px', backgroundColor: '#fff', borderRadius: '4px', border: '1px solid #e0e0e0' }}>
                                                                <div style={{ fontWeight: 'bold', color: '#1976D2', marginBottom: '8px' }}>
                                                                    🏢 {office.name} (ID: {office.id})
                                                                </div>
                                                                <div style={{ marginLeft: '12px' }}>
                                                                    <div><b>Polygons:</b> {office.polygons.length}</div>
                                                                    {office.polygons.map((polygon, polygonIndex) => {
                                                                        if (!polygon || polygon.length < 3) return null;
                                                                        const center = polygon.reduce((acc, point) => ({
                                                                            lat: acc.lat + point.lat,
                                                                            lng: acc.lng + point.lng
                                                                        }), { lat: 0, lng: 0 });
                                                                        return (
                                                                            <div key={`polygon-${officeIndex}-${polygonIndex}`} style={{ marginTop: '6px', padding: '6px', backgroundColor: '#f8f9fa', borderRadius: '4px', fontSize: '12px' }}>
                                                                                <div><b>Polygon {polygonIndex + 1}:</b> {polygon.length} vertices</div>
                                                                                <div style={{ marginTop: '2px', color: '#666' }}>Center: {`${(center.lat / polygon.length).toFixed(6)}, ${(center.lng / polygon.length).toFixed(6)}`}</div>
                                                                            </div>
                                                                        );
                                                                    })}
                                                                </div>
                                                            </div>
                                                        ))}
                                                        <div style={{ marginTop: '8px', color: 'green' }}><small>✓ Using office boundary data from API ({offices.length} office(s), {officePolygons.length} polygon(s))</small></div>
                                                    </>
                                                ) : (
                                                    <>
                                                        <div><b>Total Polygons:</b> {officePolygons.length}</div>
                                                        {officePolygons.map((polygon, index) => {
                                                            if (!polygon || polygon.length < 3) return null;
                                                            const center = polygon.reduce((acc, point) => ({
                                                                lat: acc.lat + point.lat,
                                                                lng: acc.lng + point.lng
                                                            }), { lat: 0, lng: 0 });
                                                            return (
                                                                <div key={`polygon-info-${index}`} style={{ marginTop: '8px', padding: '8px', backgroundColor: '#fff', borderRadius: '4px', border: '1px solid #e0e0e0' }}>
                                                                    <div><b>Polygon {index + 1} Center:</b> {`${(center.lat / polygon.length).toFixed(6)}, ${(center.lng / polygon.length).toFixed(6)}`}</div>
                                                                    <div style={{ marginTop: '4px' }}><b>Boundary Points:</b> {polygon.length} vertices</div>
                                                                </div>
                                                            );
                                                        })}
                                                    </>
                                                )}
                                            </>
                                        ) : (
                                            <div style={{ color: '#999' }}>
                                                No boundary data available
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Additional Information Section */}
                    <div style={{
                        backgroundColor: '#f8f9fa',
                        padding: '16px',
                        borderRadius: '8px',
                        marginBottom: '20px',
                        border: '1px solid #e9ecef'
                    }}>
                        <h5 style={{
                            margin: '0 0 16px 0',
                            color: '#1a1a1a',
                            fontWeight: '600',
                            fontSize: '15px',
                            borderBottom: '2px solid #ffc107',
                            paddingBottom: '8px'
                        }}>
                            ℹ️ Additional Information
                        </h5>
                        <div className="row">
                            <div className="col-md-6">
                                <div style={{ marginBottom: '14px' }}>
                                    <span style={{
                                        display: 'block',
                                        fontSize: '12px',
                                        color: '#6c757d',
                                        fontWeight: '500',
                                        marginBottom: '4px',
                                        textTransform: 'uppercase',
                                        letterSpacing: '0.5px'
                                    }}>
                                        Reason
                                    </span>
                                    <p style={{
                                        margin: 0,
                                        color: '#212529',
                                        fontSize: '14px',
                                        fontWeight: '500',
                                        minHeight: '20px'
                                    }}>
                                        {currentItem?.reason || "-"}
                                    </p>
                                </div>
                            </div>
                            <div className="col-md-6">
                                <div style={{ marginBottom: '14px' }}>
                                    <span style={{
                                        display: 'block',
                                        fontSize: '12px',
                                        color: '#6c757d',
                                        fontWeight: '500',
                                        marginBottom: '4px',
                                        textTransform: 'uppercase',
                                        letterSpacing: '0.5px'
                                    }}>
                                        Remarks
                                    </span>
                                    <p style={{
                                        margin: 0,
                                        color: '#212529',
                                        fontSize: '14px',
                                        fontWeight: '500',
                                        minHeight: '20px',
                                        wordBreak: 'break-word'
                                    }}>
                                        {currentItem?.remarks || "-"}
                                    </p>
                                </div>
                            </div>
                            <div className="col-md-6">
                                <div style={{ marginBottom: '14px' }}>
                                    <span style={{
                                        display: 'block',
                                        fontSize: '12px',
                                        color: '#6c757d',
                                        fontWeight: '500',
                                        marginBottom: '4px',
                                        textTransform: 'uppercase',
                                        letterSpacing: '0.5px'
                                    }}>
                                        Half Day Type
                                    </span>
                                    <p style={{
                                        margin: 0,
                                        color: '#212529',
                                        fontSize: '14px',
                                        fontWeight: '500'
                                    }}>
                                        {currentItem?.half_day_type
                                            ? currentItem.half_day_type.replace(/_/g, " ")
                                            : "-"}
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Timestamp Information Section */}
                    <div style={{
                        backgroundColor: '#f8f9fa',
                        padding: '16px',
                        borderRadius: '8px',
                        border: '1px solid #e9ecef'
                    }}>
                        <h5 style={{
                            margin: '0 0 16px 0',
                            color: '#1a1a1a',
                            fontWeight: '600',
                            fontSize: '15px',
                            borderBottom: '2px solid #6c757d',
                            paddingBottom: '8px'
                        }}>
                            🕐 Timestamp Information
                        </h5>
                        <div className="row">
                            <div className="col-md-6">
                                <div style={{ marginBottom: '14px' }}>
                                    <span style={{
                                        display: 'block',
                                        fontSize: '12px',
                                        color: '#6c757d',
                                        fontWeight: '500',
                                        marginBottom: '4px',
                                        textTransform: 'uppercase',
                                        letterSpacing: '0.5px'
                                    }}>
                                        Created At
                                    </span>
                                    <p style={{
                                        margin: 0,
                                        color: '#212529',
                                        fontSize: '14px',
                                        fontWeight: '500'
                                    }}>
                                        {currentItem?.created_at
                                            ? formatDateTime(currentItem.created_at)
                                            : "-"}
                                    </p>
                                </div>
                            </div>
                            <div className="col-md-6">
                                <div style={{ marginBottom: '14px' }}>
                                    <span style={{
                                        display: 'block',
                                        fontSize: '12px',
                                        color: '#6c757d',
                                        fontWeight: '500',
                                        marginBottom: '4px',
                                        textTransform: 'uppercase',
                                        letterSpacing: '0.5px'
                                    }}>
                                        Updated At
                                    </span>
                                    <p style={{
                                        margin: 0,
                                        color: '#212529',
                                        fontSize: '14px',
                                        fontWeight: '500'
                                    }}>
                                        {currentItem?.updated_at
                                            ? formatDateTime(currentItem.updated_at)
                                            : "-"}
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </Modal>
        </>
    );
};

export default AttendanceDetailsModel;